// Precision Liver Risk Report — premium PDF builder.
// Inspired by Apple Health / Oura / WHOOP: clean, personalized, actionable.
// Uses jsPDF only (no unicode-heavy characters — icons are drawn as shapes).

import { jsPDF } from "jspdf";
import { LOGO_PNG_B64 } from "./logoAsset.js";
import { computeLiverAge, LIVER_AGE_DISCLAIMER } from "./liverAge.js";
import { computeTargetWeight } from "./bmiTarget.js";
import { recoveryPotential } from "./recoveryPotential.js";

const RISK_HEX = {
  green: "#22C55E",
  yellow: "#EAB308",
  orange: "#F97316",
  red: "#EF4444",
};

const FOREST = [10, 46, 34];
const COPPER = [198, 123, 92];
const BEIGE = [235, 232, 216];
const CANVAS = [251, 249, 244];
const TEXT_DARK = [10, 10, 10];
const TEXT_MUTED = [90, 100, 96];
const WHITE = [255, 255, 255];

const CONTACT_PHONE = "+91 78619 17281";
const WHATSAPP_NUMBER = "917861917281";
const PRICE_LABEL = "Basic INR 4,999 · Advance INR 7,999";

// Band → { action title, description, follow-up cadence, icon type }
const NEXT_ACTION_BY_BAND = {
  green: {
    title: "Keep it up — no assessment needed",
    description: "You're doing great. Maintain your habits and re-check your Liver Risk Score in 12 months.",
    icon: "shield",
    hideCta: true,
  },
  yellow: {
    title: "Book a Basic Precision Liver Evaluation",
    description: "A short physician-led review to establish your baseline liver markers and rule out early fatty liver.",
    icon: "stethoscope",
  },
  orange: {
    title: "Book an Advance Precision Liver Evaluation",
    description: "FibroScan-based liver stiffness, fatty-liver quantification, and a personalised 12-week roadmap.",
    icon: "scan",
  },
  red: {
    title: "Book a Priority Physician Consultation",
    description: "A physician-led consultation combined with an Advance Precision Liver Evaluation. Do not delay.",
    icon: "alert",
  },
};

const PHILOSOPHY = [
  { label: "Assess", body: "Digital screening" },
  { label: "Stratify", body: "Risk banding" },
  { label: "Personalize", body: "Physician plan" },
  { label: "Restore", body: "Guided recovery" },
  { label: "Sustain", body: "Longitudinal care" },
];

// ------------------------------------------------------------------
// Small utility helpers
// ------------------------------------------------------------------
const hexToRgb = (hex) => {
  const h = hex.replace("#", "");
  return { r: parseInt(h.substring(0, 2), 16), g: parseInt(h.substring(2, 4), 16), b: parseInt(h.substring(4, 6), 16) };
};

// jsPDF's default Helvetica encoding mangles a small set of non-Latin-1
// characters (notably ≤, ≥, ≈, arrows). Everything else — including en/em
// dashes and curly quotes — renders fine, so we only sanitize the risky
// glyphs to preserve typography.
const UNICODE_MAP = {
  "\u2264": "<=",   // ≤
  "\u2265": ">=",   // ≥
  "\u2248": "~",    // ≈
  "\u2192": "->",   // →
  "\u2190": "<-",   // ←
};
const safe = (s) => {
  if (s == null) return "";
  return String(s).replace(/[\u2190\u2192\u2248\u2264\u2265]/g, (c) => UNICODE_MAP[c] ?? c);
};

const ageBandFromAnswer = (id) => ({ a: "Under 35", b: "35-44", c: "45-54", d: "55+" }[id] || "—");
const ageDisplay = (contact, answers) => {
  const y = Number(contact?.ageYears);
  if (Number.isFinite(y) && y > 0) return String(Math.round(y));
  return ageBandFromAnswer(answers?.age);
};

const buildWhatsAppUrl = (result, contact, answers) => {
  const cfg = NEXT_ACTION_BY_BAND[result.band];
  if (!cfg || cfg.hideCta) return null;
  const fullName = `${contact.firstName || ""} ${contact.lastName || ""}`.trim() || "Not shared";
  const age = ageDisplay(contact, answers);
  const sex = contact.gender || "Not shared";
  const phone = contact.phone || "Not shared";
  const shortId = String(result.id || "").split("-")[0].toUpperCase();
  const msg = [
    "Hi SuperAge Health,",
    "",
    `I just completed the Liver Risk Assessment and would like to *${cfg.title}*.`,
    "",
    "Please find my details below:",
    `• Name: ${fullName}`,
    `• Age: ${age} yrs`,
    `• Sex: ${sex}`,
    `• Phone: ${phone}`,
    "",
    `Report ID: ${shortId}`,
    `Liver Risk Score: ${result.score}/100`,
    `Risk Band: ${result.label}`,
    "",
    "Please call me back to coordinate.",
    "Thank you.",
  ].join("\n");
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
};

// Generate a personalized headline based on band + top factor + name.
const personalizedHeadline = (result, contact) => {
  const firstName = (contact.firstName || "").trim();
  const nameOr = firstName || "you";
  switch (result.band) {
    case "green":
      return `${firstName ? firstName + "," : "You're"} your liver is in great shape.`;
    case "yellow":
      return `${firstName ? firstName + "," : ""} your liver needs early attention.`;
    case "orange":
      return `${firstName ? firstName + "," : ""} your liver is under meaningful strain.`;
    case "red":
    default:
      if (result.red_flag_level === "emergency")
        return `${firstName || "Urgent"}: seek emergency medical care now.`;
      return `${firstName ? firstName + "," : ""} act on this without delay.`;
  }
};

// Generate a personalized summary using top contributor if present.
const personalizedSummary = (result) => {
  const top = result.top_contributors && result.top_contributors[0];
  if (result.band === "green") {
    return "Your assessment suggests a low current liver-metabolic risk profile. The goal now is to sustain and protect what's already working.";
  }
  if (top) {
    const drivers = (result.top_contributors || []).slice(0, 2).map((c) => c.question.toLowerCase()).join(" and ");
    if (result.band === "yellow") {
      return `Early risk factors — most notably your ${drivers} — are placing measurable load on your liver. Acting now is highly effective.`;
    }
    if (result.band === "orange") {
      return `Multiple factors, led by your ${drivers}, point to a materially elevated risk of fatty liver or early fibrosis. A precision workup is strongly advised.`;
    }
    return `Your profile — driven by your ${drivers} and others — indicates a high probability of advanced liver stress. Objective assessment should not be delayed.`;
  }
  return result.summary || "";
};

// ------------------------------------------------------------------
// Icon primitives (drawn as vector shapes — no unicode fonts needed)
// ------------------------------------------------------------------
const drawCheck = (doc, cx, cy, size = 6, color = WHITE) => {
  doc.setDrawColor(...color);
  doc.setLineWidth(size / 5);
  doc.line(cx - size * 0.5, cy, cx - size * 0.15, cy + size * 0.4);
  doc.line(cx - size * 0.15, cy + size * 0.4, cx + size * 0.55, cy - size * 0.35);
};

const drawArrow = (doc, cx, cy, size = 8, color = WHITE) => {
  doc.setFillColor(...color);
  doc.triangle(cx - size * 0.4, cy - size * 0.5, cx - size * 0.4, cy + size * 0.5, cx + size * 0.55, cy, "F");
};

const drawCalendar = (doc, cx, cy, size = 8, color = WHITE) => {
  doc.setDrawColor(...color);
  doc.setLineWidth(1);
  doc.setFillColor(...color);
  const w = size * 1.2;
  const h = size;
  doc.roundedRect(cx - w / 2, cy - h / 2 + 1, w, h, 1.2, 1.2, "S");
  // top pegs
  doc.line(cx - w / 4, cy - h / 2 - 1, cx - w / 4, cy - h / 2 + 3);
  doc.line(cx + w / 4, cy - h / 2 - 1, cx + w / 4, cy - h / 2 + 3);
  // top line
  doc.line(cx - w / 2, cy - h / 2 + 4, cx + w / 2, cy - h / 2 + 4);
};

// Draw the SuperAge infinity-mark logo (lemniscate). Two rotated,
// stroked ellipses that cross at center — copper on the left, forest on
// the right — echoing the SVG on the landing page.
const drawInfinityMark = (doc, cx, cy, size, colors = { left: COPPER, right: FOREST }) => {
  // Approximate a rotated ellipse using 4 cubic bezier segments.
  const drawRotatedEllipse = (rcx, rcy, rx, ry, angle, color) => {
    const k = 0.5522847498;
    const rot = (x, y) => ({
      x: rcx + (x - rcx) * Math.cos(angle) - (y - rcy) * Math.sin(angle),
      y: rcy + (x - rcx) * Math.sin(angle) + (y - rcy) * Math.cos(angle),
    });
    const p = [
      rot(rcx + rx, rcy),
      rot(rcx, rcy + ry),
      rot(rcx - rx, rcy),
      rot(rcx, rcy - ry),
    ];
    const c = [
      [rot(rcx + rx, rcy + k * ry), rot(rcx + k * rx, rcy + ry)],
      [rot(rcx - k * rx, rcy + ry), rot(rcx - rx, rcy + k * ry)],
      [rot(rcx - rx, rcy - k * ry), rot(rcx - k * rx, rcy - ry)],
      [rot(rcx + k * rx, rcy - ry), rot(rcx + rx, rcy - k * ry)],
    ];
    const segs = [];
    for (let i = 0; i < 4; i++) {
      const start = p[i];
      const end = p[(i + 1) % 4];
      const c1 = c[i][0];
      const c2 = c[i][1];
      segs.push([c1.x - start.x, c1.y - start.y, c2.x - start.x, c2.y - start.y, end.x - start.x, end.y - start.y]);
    }
    doc.setDrawColor(...color);
    doc.lines(segs, p[0].x, p[0].y, [1, 1], "S", false);
  };

  const rx = size * 0.42;        // lobe horizontal radius
  const ry = size * 0.22;        // lobe vertical radius
  const offset = size * 0.30;    // horizontal shift of each lobe centre
  const tilt = Math.PI / 6;      // 30° tilt
  doc.setLineWidth(size * 0.10);
  doc.setLineCap?.(1);
  doc.setLineJoin?.(1);
  // Left ribbon — tilted so it rises from bottom-left to upper-right (copper)
  drawRotatedEllipse(cx - offset, cy, rx, ry, -tilt, colors.left);
  // Right ribbon — tilted the opposite way (forest green)
  drawRotatedEllipse(cx + offset, cy, rx, ry, tilt, colors.right);
};

// ------------------------------------------------------------------
// Recovery Plan page — 5 sections + priority action
// ------------------------------------------------------------------
// Icon glyphs drawn as small vector shapes on a copper disc.
const drawSectionIcon = (doc, cx, cy, r, kind) => {
  doc.setFillColor(...COPPER);
  doc.circle(cx, cy, r, "F");
  const c = WHITE;
  const s = r * 0.9;
  if (kind === "salad") {
    doc.setDrawColor(...c);
    doc.setLineWidth(1.4);
    doc.line(cx - s * 0.75, cy + s * 0.25, cx + s * 0.75, cy + s * 0.25);
    doc.line(cx - s * 0.75, cy + s * 0.25, cx - s * 0.4, cy + s * 0.7);
    doc.line(cx + s * 0.75, cy + s * 0.25, cx + s * 0.4, cy + s * 0.7);
    doc.setFillColor(...c);
    doc.circle(cx - s * 0.35, cy + s * 0.05, s * 0.2, "F");
    doc.circle(cx, cy - s * 0.05, s * 0.22, "F");
    doc.circle(cx + s * 0.35, cy + s * 0.05, s * 0.2, "F");
  } else if (kind === "activity") {
    doc.setFillColor(...c);
    doc.roundedRect(cx - s * 0.6, cy - s * 0.35, s * 0.25, s * 0.7, 1.5, 1.5, "F");
    doc.roundedRect(cx + s * 0.35, cy - s * 0.35, s * 0.25, s * 0.7, 1.5, 1.5, "F");
    doc.rect(cx - s * 0.35, cy - s * 0.08, s * 0.7, s * 0.16, "F");
  } else if (kind === "stethoscope") {
    doc.setDrawColor(...c);
    doc.setLineWidth(1.4);
    doc.line(cx - s * 0.55, cy, cx + s * 0.55, cy);
    doc.line(cx, cy - s * 0.55, cx, cy + s * 0.55);
    doc.setFillColor(...c);
    doc.circle(cx, cy, s * 0.15, "F");
  } else if (kind === "microscope") {
    doc.setDrawColor(...c);
    doc.setLineWidth(1.6);
    doc.circle(cx - s * 0.15, cy - s * 0.15, s * 0.45, "S");
    doc.line(cx + s * 0.2, cy + s * 0.2, cx + s * 0.6, cy + s * 0.6);
  } else if (kind === "pill") {
    // Rotated pill capsule shape
    doc.setFillColor(...c);
    doc.roundedRect(cx - s * 0.6, cy - s * 0.3, s * 1.2, s * 0.6, s * 0.3, s * 0.3, "F");
    // Center divider indicating the two halves
    doc.setDrawColor(...COPPER);
    doc.setLineWidth(1.2);
    doc.line(cx, cy - s * 0.28, cx, cy + s * 0.28);
  } else if (kind === "trending-up") {
    doc.setDrawColor(...c);
    doc.setLineWidth(1.6);
    doc.line(cx - s * 0.6, cy + s * 0.45, cx - s * 0.15, cy);
    doc.line(cx - s * 0.15, cy, cx + s * 0.2, cy + s * 0.15);
    doc.line(cx + s * 0.2, cy + s * 0.15, cx + s * 0.65, cy - s * 0.4);
    doc.setFillColor(...c);
    doc.triangle(cx + s * 0.65, cy - s * 0.4, cx + s * 0.4, cy - s * 0.4, cx + s * 0.65, cy - s * 0.15, "F");
  }
};

function drawRecoveryPlan(doc, y, M, W, H, plan, bandArr) {
  // Section header — same style as "WHY YOU RECEIVED THIS SCORE"
  sectionHeader(doc, "PERSONALIZED LIVER RECOVERY RECOMMENDATIONS", M, y, W, M);
  y += 12;

  const usableW = W - M * 2;
  let curY = y;

  // Each section renders as a warm-tinted horizontal card matching the
  // Risk factor row aesthetic — soft beige fill, copper icon disc, bold
  // title, muted body sentence.
  const drawStackedSection = (s, x, y0, w) => {
    const iconR = 8;
    const topPad = 8;
    const bottomPad = 7;
    // Compose the sentence: items joined by " • "
    const joined = s.items.map((it) => it.replace(/\.$/, "")).join("  •  ");
    const bodyX = x + 14 + iconR * 2 + 10;
    const bodyMaxW = w - (bodyX - x) - 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const bodyLines = doc.splitTextToSize(joined, bodyMaxW);
    const titleLineH = 11;
    const bodyLineH = 9.5;
    const h = topPad + titleLineH + 2 + bodyLines.length * bodyLineH + bottomPad;

    // Warm beige tint fill (like Risk factor rows)
    doc.setFillColor(...BEIGE);
    doc.setGState(new doc.GState({ opacity: 0.55 }));
    doc.roundedRect(x, y0, w, h, 8, 8, "F");
    doc.setGState(new doc.GState({ opacity: 1 }));
    // Subtle border
    doc.setDrawColor(...BEIGE);
    doc.setLineWidth(0.6);
    doc.roundedRect(x, y0, w, h, 8, 8, "S");

    // Copper icon disc
    drawSectionIcon(doc, x + 14 + iconR, y0 + topPad + iconR, iconR, s.icon);
    // Title — bold, forest
    doc.setTextColor(...FOREST);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.text(s.title, bodyX, y0 + topPad + iconR + 3);
    // Body — muted forest
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...TEXT_MUTED);
    doc.text(bodyLines, bodyX, y0 + topPad + titleLineH + 11, { lineHeightFactor: 1.3 });
    return h;
  };

  const rowGap = 5;
  plan.sections.forEach((s) => {
    const h = drawStackedSection(s, M, curY, usableW);
    curY += h + rowGap;
  });
  return curY + 2;
}

// ------------------------------------------------------------------
// Section header — small caps + subtle underline
// ------------------------------------------------------------------
const sectionHeader = (doc, label, x, y, W, M) => {
  doc.setTextColor(...COPPER);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.text(label, x, y);
  const tw = doc.getTextWidth(label);
  doc.setDrawColor(...COPPER);
  doc.setLineWidth(0.7);
  doc.line(x, y + 3, x + tw, y + 3);
};

// ------------------------------------------------------------------
// MAIN BUILDER
// ------------------------------------------------------------------
export const buildLiverReport = (result, contact, answers) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  // Auto-sanitize every text write so that non-Latin-1 glyphs never mangle output.
  const _rawText = doc.text.bind(doc);
  doc.text = (text, x, y, opts) => {
    if (Array.isArray(text)) return _rawText(text.map(safe), x, y, opts);
    return _rawText(safe(text), x, y, opts);
  };
  const _rawLink = doc.textWithLink?.bind(doc);
  if (_rawLink) {
    doc.textWithLink = (text, x, y, opts) => _rawLink(safe(text), x, y, opts);
  }
  const _rawSplit = doc.splitTextToSize.bind(doc);
  doc.splitTextToSize = (text, w, opts) => _rawSplit(safe(text), w, opts);
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 40;
  const shortId = String(result.id || "").split("-")[0].toUpperCase();
  const bandRgb = hexToRgb(RISK_HEX[result.band] || RISK_HEX.green);
  const bandArr = [bandRgb.r, bandRgb.g, bandRgb.b];

  // ============================ SINGLE-PAGE REPORT ============================
  drawHeaderBand(doc, W, M, shortId);
  let y = 84;

  y = drawPatientStrip(doc, y, M, W, contact, answers, result);
  y += 6;
  y = drawHeroScoreCard(doc, y, M, W, result, contact, bandArr);
  y = drawWhyYouReceived(doc, y, M, W, result, bandArr);
  y = drawBmiBmr(doc, y, M, W, contact, bandArr);
  if (result.recovery_plan) {
    y = drawRecoveryPlan(doc, y, M, W, H, result.recovery_plan, bandArr);
  }
  y = drawNextAction(doc, y, M, W, result, contact, answers, bandArr);
  const reserveBottom = measureDisclaimerReserve(doc, M, W);
  y = drawPhilosophy(doc, y, M, W, H, reserveBottom);
  drawDisclaimer(doc, y + 4, M, W, H);
  drawFooter(doc, M, W, H);

  return { doc, shortId };
};

// ------------------------------------------------------------------
// Page-level renderers
// ------------------------------------------------------------------
function drawHeaderBand(doc, W, M, shortId) {
  // Compact header — brand lockup left, report meta right.
  // Slimmed from 84 → 72pt to free vertical space so the single-page
  // report can accommodate the optional Precision Therapeutics section
  // without overlapping the footer, while keeping the brand tagline.
  const headerH = 72;
  doc.setFillColor(...CANVAS);
  doc.rect(0, 0, W, headerH, "F");
  doc.setDrawColor(...BEIGE);
  doc.setLineWidth(0.6);
  doc.line(0, headerH, W, headerH);

  // Infinity-mark logo — embedded brand PNG (118x82 → aspect 1.44)
  const logoW = 46;
  const logoH = logoW / (118 / 82);
  const logoX = M;
  const logoY = (headerH - logoH) / 2 - 3;
  doc.addImage(LOGO_PNG_B64, "PNG", logoX, logoY, logoW, logoH, undefined, "FAST");

  // Wordmark — "SuperAge" (forest) + "Health" (copper)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...FOREST);
  const wordX = logoX + logoW + 8;
  const wordY = headerH / 2 + 1;
  doc.text("SuperAge", wordX, wordY);
  const saWidth = doc.getTextWidth("SuperAge ");
  doc.setTextColor(...COPPER);
  doc.text("Health", wordX + saWidth, wordY);

  // Tagline — centered under the wordmark with small dashes flanking
  const fullWordW = doc.getTextWidth("SuperAge Health");
  const taglineCX = wordX + fullWordW / 2;
  const taglineY = wordY + 12;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(...TEXT_MUTED);
  const taglineText = "PRECISION ORGAN MEDICINE";
  doc.text(taglineText, taglineCX, taglineY, { align: "center" });
  const taglineW = doc.getTextWidth(taglineText);
  const dashLen = 7;
  const dashGap = 5;
  const dashY = taglineY - 2;
  doc.setDrawColor(...TEXT_MUTED);
  doc.setLineWidth(0.6);
  doc.line(taglineCX - taglineW / 2 - dashGap - dashLen, dashY, taglineCX - taglineW / 2 - dashGap, dashY);
  doc.line(taglineCX + taglineW / 2 + dashGap, dashY, taglineCX + taglineW / 2 + dashGap + dashLen, dashY);

  // Report meta — right (compact)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(...COPPER);
  doc.text("PRECISION LIVER RISK REPORT", W - M, 24, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...FOREST);
  doc.text(`REPORT ID · ${shortId}`, W - M, 40, { align: "right" });
  doc.setTextColor(...TEXT_MUTED);
  doc.text(
    new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }),
    W - M, 56, { align: "right" }
  );
}

function drawPatientStrip(doc, y, M, W, contact, answers, result) {
  const fullName = `${contact.firstName || ""} ${contact.lastName || ""}`.trim() || "—";
  const age = (Number.isFinite(Number(contact?.ageYears)) && Number(contact.ageYears) > 0)
    ? `${Math.round(Number(contact.ageYears))} yrs`
    : (answers?.age ? `${ageBandFromAnswer(answers.age)} yrs` : "—");
  const sex = contact.gender || "—";
  const phone = contact.phone || "—";
  const cols = [
    { label: "NAME", value: fullName },
    { label: "AGE", value: age },
    { label: "SEX", value: sex },
    { label: "CONTACT", value: phone },
  ];
  const colW = (W - M * 2) / cols.length;
  cols.forEach((c, i) => {
    const x = M + i * colW;
    doc.setTextColor(...TEXT_MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.text(c.label, x, y);
    doc.setTextColor(...FOREST);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(String(c.value), x, y + 15);
    if (i < cols.length - 1) {
      doc.setDrawColor(...BEIGE);
      doc.setLineWidth(0.8);
      doc.line(x + colW - 12, y - 6, x + colW - 12, y + 18);
    }
  });
  return y + 30;
}

/** Draw a filled or outlined 5-point star centred at (cx, cy). Uses jsPDF's
 *  `doc.lines` polygon primitive because jsPDF's built-in Helvetica doesn't
 *  render unicode ★/☆ glyphs.
 */
function drawStar(doc, cx, cy, outerR, innerR, fillColor, strokeColor) {
  const points = [];
  for (let i = 0; i < 10; i++) {
    const angle = (Math.PI / 5) * i - Math.PI / 2;
    const r = i % 2 === 0 ? outerR : innerR;
    points.push([cx + Math.cos(angle) * r, cy + Math.sin(angle) * r]);
  }
  const deltas = [];
  for (let i = 1; i < points.length; i++) {
    deltas.push([points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]]);
  }
  if (fillColor) {
    doc.setFillColor(...fillColor);
    doc.setDrawColor(...fillColor);
    doc.setLineWidth(0.3);
    doc.lines(deltas, points[0][0], points[0][1], [1, 1], "F", true);
  } else {
    doc.setDrawColor(...(strokeColor || [200, 200, 200]));
    doc.setLineWidth(0.5);
    doc.lines(deltas, points[0][0], points[0][1], [1, 1], "S", true);
  }
}


function drawHeroScoreCard(doc, y, M, W, result, contact, bandArr) {
  const cardH = 116;
  doc.setFillColor(...BEIGE);
  doc.roundedRect(M, y, W - M * 2, cardH, 14, 14, "F");

  // Score circle — top-left, sits above the risk scale
  const circleR = 30;
  const cx = M + 46;
  const cy = y + 44;
  doc.setFillColor(...WHITE);
  doc.circle(cx, cy, circleR, "F");
  doc.setDrawColor(...bandArr);
  doc.setLineWidth(4.5);
  doc.circle(cx, cy, circleR, "S");
  doc.setTextColor(...FOREST);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text(String(result.score), cx, cy + 3, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...TEXT_MUTED);
  doc.text("/ 100", cx, cy + 14, { align: "center" });

  // SuperAge Liver Age™ block — right column, mirrors the score circle's
  // visual weight so the card reads as [SCORE] [MESSAGE] [LIVER AGE].
  const liverAge = computeLiverAge(result.score, contact?.ageYears ?? contact?.age_years);
  const rightColW = 110;
  const rightColX = W - M - rightColW - 10;
  let messageMaxRight = W - M - 20;
  if (liverAge) {
    // Subtle vertical divider between message column and liver-age column
    doc.setDrawColor(...COPPER);
    doc.setLineWidth(0.6);
    doc.setGState(new doc.GState({ opacity: 0.35 }));
    doc.line(rightColX - 6, y + 20, rightColX - 6, y + cardH - 34);
    doc.setGState(new doc.GState({ opacity: 1 }));

    // Copper micro-label
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    doc.setTextColor(...COPPER);
    doc.text("SUPERAGE LIVER AGE", rightColX + rightColW / 2, y + 26, { align: "center" });
    // Big value: ~52
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(...FOREST);
    const valueStr = `\u2248 ${liverAge.liverAge}`;
    doc.text(valueStr, rightColX + rightColW / 2, y + 52, { align: "center" });
    // yrs unit
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...TEXT_MUTED);
    doc.text("years", rightColX + rightColW / 2, y + 62, { align: "center" });
    // Fine caption below — aligns with bottom of score circle
    doc.setFont("helvetica", "italic");
    doc.setFontSize(6.2);
    doc.setTextColor(...TEXT_MUTED);
    const caption = liverAge.penalty === 0
      ? "aging on track"
      : `+${liverAge.penalty} yrs vs. chronological`;
    doc.text(caption, rightColX + rightColW / 2, y + 74, { align: "center" });
    messageMaxRight = rightColX - 12;
  }

  // Zone pill (moved beside the score, aligns with middle column)
  const pillX = M + 96;
  const pillY = y + 14;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  const pillLabel = result.label.toUpperCase();
  const pillW = Math.max(110, doc.getTextWidth(pillLabel) + 22);
  doc.setFillColor(...bandArr);
  doc.roundedRect(pillX, pillY, pillW, 18, 9, 9, "F");
  doc.setTextColor(...WHITE);
  doc.text(pillLabel, pillX + pillW / 2, pillY + 12, { align: "center" });

  // Recovery Potential chip — mirrors the on-page results chip so the star
  // rating for reversibility is also visible on the printout. Sits on the
  // same row as the zone pill, immediately to its right.
  const rp = recoveryPotential(result.score, result.band);
  if (rp) {
    const chipH = 18;
    const chipX = pillX + pillW + 8;
    const chipY = pillY;
    const microLabel = "RECOVERY";
    const valueLabel = rp.label.toUpperCase();
    // Measure chip contents
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6);
    const microW = doc.getTextWidth(microLabel);
    doc.setFontSize(8);
    const valueW = doc.getTextWidth(valueLabel);
    // 5 stars, ~7pt each with 1pt gap = 39pt
    const starsW = 5 * 6.5 + 4;
    const padL = 8;
    const gap = 4;
    const chipW = padL + microW + gap + starsW + gap + valueW + padL;
    // Rounded rect frame
    doc.setFillColor(...WHITE);
    doc.setDrawColor(...BEIGE);
    doc.setLineWidth(0.6);
    doc.roundedRect(chipX, chipY, chipW, chipH, chipH / 2, chipH / 2, "FD");
    // Micro label
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6);
    doc.setTextColor(...TEXT_MUTED);
    doc.text(microLabel, chipX + padL, chipY + 12);
    // Stars — drawn as proper 5-point star polygons because jsPDF's default
    // Helvetica does not render unicode ★/☆ glyphs.
    const starsX = chipX + padL + microW + gap;
    const starOuter = 2.8;
    const starInner = 1.15;
    const starPitch = 6.5;
    for (let i = 0; i < 5; i++) {
      const filled = i < rp.stars;
      const cx = starsX + i * starPitch + starOuter;
      const cy = chipY + chipH / 2;
      drawStar(
        doc,
        cx,
        cy,
        starOuter,
        starInner,
        filled ? COPPER : null,
        filled ? null : BEIGE,
      );
    }
    // Value label
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...FOREST);
    doc.text(valueLabel, starsX + starsW + gap, chipY + 12);
  }

  // Personalized headline — width now constrained by the right column
  doc.setTextColor(...FOREST);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  const headline = personalizedHeadline(result, contact);
  const headlineMaxW = Math.max(120, messageMaxRight - pillX);
  const headlineLines = doc.splitTextToSize(headline, headlineMaxW).slice(0, 2);
  doc.text(headlineLines, pillX, pillY + 34);

  // Personalized summary
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...TEXT_MUTED);
  const summary = personalizedSummary(result);
  const summaryLines = doc.splitTextToSize(summary, headlineMaxW).slice(0, 2);
  doc.text(summaryLines, pillX, pillY + 34 + headlineLines.length * 14 + 4, { lineHeightFactor: 1.35 });

  // ------ Risk-zone horizontal scale — sits UNDER the score, spans full card width ------
  const scaleY = y + cardH - 24;
  const scaleH = 6;
  const scaleX = M + 20;
  const scaleW = W - M * 2 - 40;
  const zones = [
    { hex: "#22C55E", w: 0.20, label: "LOW" },
    { hex: "#EAB308", w: 0.20, label: "MODERATE" },
    { hex: "#F97316", w: 0.20, label: "HIGH" },
    { hex: "#EF4444", w: 0.40, label: "SEVERE" },
  ];
  let px = scaleX;
  const zoneCenters = [];
  zones.forEach((z, i) => {
    const zw = scaleW * z.w;
    const rgb = hexToRgb(z.hex);
    doc.setFillColor(rgb.r, rgb.g, rgb.b);
    doc.setGState(new doc.GState({ opacity: 0.35 }));
    doc.roundedRect(px, scaleY, zw, scaleH, scaleH / 2, scaleH / 2, "F");
    doc.setGState(new doc.GState({ opacity: 1 }));
    zoneCenters.push({ cx: px + zw / 2, label: z.label });
    px += zw + (i < zones.length - 1 ? 2 : 0);
  });
  // Marker dot
  const scoreClamped = Math.min(100, Math.max(0, result.score));
  const markerX = scaleX + (scaleW * scoreClamped) / 100;
  doc.setFillColor(...bandArr);
  doc.circle(markerX, scaleY + scaleH / 2, 4, "F");
  doc.setFillColor(...WHITE);
  doc.circle(markerX, scaleY + scaleH / 2, 1.6, "F");
  // Zone labels below the bar
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6);
  zoneCenters.forEach((z) => {
    const isActive = markerX >= (z.cx - scaleW / 10) && markerX <= (z.cx + scaleW / 10);
    doc.setTextColor(...(isActive ? bandArr : TEXT_MUTED));
    doc.text(z.label, z.cx, scaleY + scaleH + 8, { align: "center" });
  });

  return y + cardH + 8;
}

function drawWhyYouReceived(doc, y, M, W, result, bandArr) {
  // Header removed per user request — content shifts up to reclaim ~14pt of
  // vertical space and tighten the layout.
  const gap = 12;
  const colW = (W - M * 2 - gap) / 2;

  // Left col — RISK FACTORS
  const riskItems = (result.top_contributors || []).slice(0, 4);
  const rightItems = (result.top_protective || []).slice(0, 4);
  const rowCount = Math.max(riskItems.length, rightItems.length, 1);
  const rowH = 22;
  const boxH = 28 + rowCount * (rowH + 3);

  // LEFT — Risk factors
  doc.setFillColor(...WHITE);
  doc.setDrawColor(...BEIGE);
  doc.setLineWidth(0.8);
  doc.roundedRect(M, y, colW, boxH, 10, 10, "FD");
  doc.setTextColor(...FOREST);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("Risk factors", M + 12, y + 15);
  // Dot indicator
  doc.setFillColor(...bandArr);
  doc.circle(M + colW - 12, y + 12, 3, "F");
  if (riskItems.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8.5);
    doc.setTextColor(...TEXT_MUTED);
    doc.text("No standout drivers - nice work.", M + 12, y + 34);
  } else {
    let ry = y + 26;
    riskItems.forEach((c) => {
      doc.setFillColor(...bandArr);
      doc.setGState(new doc.GState({ opacity: 0.12 }));
      doc.roundedRect(M + 6, ry, colW - 12, rowH, 5, 5, "F");
      doc.setGState(new doc.GState({ opacity: 1 }));
      doc.setFillColor(...bandArr);
      doc.circle(M + 16, ry + rowH / 2, 2.5, "F");
      doc.setTextColor(...FOREST);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.text(c.question, M + 24, ry + 9);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(...TEXT_MUTED);
      doc.text(c.answer, M + 24, ry + 18);
      ry += rowH + 3;
    });
  }

  // RIGHT — Protective factors
  const rx = M + colW + gap;
  const greenRgb = hexToRgb("#22C55E");
  const greenArr = [greenRgb.r, greenRgb.g, greenRgb.b];
  doc.setFillColor(...WHITE);
  doc.setDrawColor(...BEIGE);
  doc.roundedRect(rx, y, colW, boxH, 10, 10, "FD");
  doc.setTextColor(...FOREST);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("Protective factors", rx + 12, y + 15);
  doc.setFillColor(...greenArr);
  doc.circle(rx + colW - 12, y + 12, 3, "F");
  if (rightItems.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8.5);
    doc.setTextColor(...TEXT_MUTED);
    doc.text("A workup can reveal your strengths.", rx + 12, y + 34);
  } else {
    let py = y + 26;
    rightItems.forEach((p) => {
      doc.setFillColor(...greenArr);
      doc.setGState(new doc.GState({ opacity: 0.10 }));
      doc.roundedRect(rx + 6, py, colW - 12, rowH, 5, 5, "F");
      doc.setGState(new doc.GState({ opacity: 1 }));
      // Green check circle
      doc.setFillColor(...greenArr);
      doc.circle(rx + 16, py + rowH / 2, 5, "F");
      drawCheck(doc, rx + 16, py + rowH / 2, 5, WHITE);
      doc.setTextColor(...FOREST);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.text(p.label, rx + 26, py + rowH / 2 + 3);
      py += rowH + 3;
    });
  }

  return y + boxH + 10;
}

// BMI band colors (Asian / WHO Asia-Pacific consensus) — same palette as the
// results page so patients see a consistent visual across screen + PDF.
const BMI_BANDS_PDF = [
  { min: 0,    max: 18.5, label: "UNDER",  hex: [96, 165, 250] },
  { min: 18.5, max: 23,   label: "NORMAL", hex: [34, 197, 94]  },
  { min: 23,   max: 25,   label: "OVER",   hex: [234, 179, 8]  },
  { min: 25,   max: 30,   label: "OBESE",  hex: [249, 115, 22] },
  { min: 30,   max: 50,   label: "MORBID", hex: [239, 68, 68]  },
];
const BMI_LABELS = {
  UNDER: "Underweight",
  NORMAL: "Normal",
  OVER: "Overweight",
  OBESE: "Obese",
  MORBID: "Morbidly Obese",
};

function drawBmiBmr(doc, y, M, W, contact, bandArr) {
  // Cards sit directly under the "Why you received this score" block. Height
  // grown from 62 → 82pt so the BMI + BMR content breathes and the wasted gap
  // above the Assess/Stratify pathway (which is bottom-anchored) is reclaimed.
  const cardH = 82;
  const gap = 10;
  const cardW = (W - M * 2 - gap) / 2;
  const bmi = contact?.bmiValue ?? contact?.bmi_value ?? null;
  const heightCm = contact?.heightCm ?? contact?.height_cm ?? null;
  const weightKg = contact?.weightKg ?? contact?.weight_kg ?? null;
  const age = contact?.ageYears ?? contact?.age_years ?? null;
  const gender = (contact?.gender || "").toLowerCase();

  // ---- BMI card (left) ----
  doc.setFillColor(...WHITE);
  doc.setDrawColor(...BEIGE);
  doc.setLineWidth(0.8);
  doc.roundedRect(M, y, cardW, cardH, 8, 8, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(...COPPER);
  doc.text("BODY MASS INDEX", M + 10, y + 13);

  if (bmi != null) {
    const band = BMI_BANDS_PDF.find((b) => bmi >= b.min && bmi < b.max) || BMI_BANDS_PDF[BMI_BANDS_PDF.length - 1];
    // Big number
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(...FOREST);
    doc.text(bmi.toFixed(1), M + 10, y + 33);
    // Band pill — placed on a NEW LINE beneath the number to guarantee no overlap
    const pillLabel = BMI_LABELS[band.label] || band.label;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    const pillW = doc.getTextWidth(pillLabel) + 12;
    doc.setFillColor(...band.hex);
    doc.roundedRect(M + 10, y + 40, pillW, 11, 5.5, 5.5, "F");
    doc.setTextColor(...WHITE);
    doc.text(pillLabel, M + 10 + 6, y + 47);

    // ---- Right-side grouped info block: [h/w sub] ─ [TARGET · kg · delta] ----
    // A clean vertical stack with a subtle divider between height/weight and
    // the target action, so the block reads as an intentional mini-panel
    // instead of two floaty lines.
    const rBoxRight = M + cardW - 8;
    const rBoxLeft  = M + cardW * 0.42;
    const rBoxTop   = y + 9;
    const rBoxBot   = y + 54;
    // Subtle rounded background so the info feels grouped
    doc.setFillColor(...CANVAS);
    doc.setDrawColor(...BEIGE);
    doc.setLineWidth(0.5);
    doc.roundedRect(rBoxLeft, rBoxTop, rBoxRight - rBoxLeft, rBoxBot - rBoxTop, 5, 5, "FD");

    // Row 1 — height + weight
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6);
    doc.setTextColor(...TEXT_MUTED);
    doc.text("HEIGHT · WEIGHT", rBoxLeft + 6, rBoxTop + 8);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...FOREST);
    const sub = [heightCm ? `${Math.round(heightCm)} cm` : "", weightKg ? `${Math.round(weightKg)} kg` : ""].filter(Boolean).join(" · ");
    if (sub) doc.text(sub, rBoxRight - 6, rBoxTop + 8, { align: "right" });

    // Divider
    doc.setDrawColor(...BEIGE);
    doc.setLineWidth(0.5);
    doc.line(rBoxLeft + 6, rBoxTop + 20, rBoxRight - 6, rBoxTop + 20);

    // Row 2 — target
    const tgt = computeTargetWeight(heightCm, weightKg);
    if (tgt) {
      const verb = tgt.action === "lose" ? "Lose" : tgt.action === "gain" ? "Gain" : null;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6);
      doc.setTextColor(...COPPER);
      doc.text("TARGET WEIGHT", rBoxLeft + 6, rBoxTop + 30);
      // Big target kg
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(...FOREST);
      doc.text(`~${tgt.targetKg} kg`, rBoxLeft + 6, rBoxTop + 41);
      // Action pill on the right side of the row
      const actionText = verb ? `${verb} ~${tgt.deltaKg} kg` : "On target";
      const actionColor = verb ? band.hex : [34, 197, 94];
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      const actionW = doc.getTextWidth(actionText) + 10;
      const actionX = rBoxRight - 6 - actionW;
      doc.setFillColor(...actionColor);
      doc.roundedRect(actionX, rBoxTop + 32, actionW, 11, 5.5, 5.5, "F");
      doc.setTextColor(...WHITE);
      doc.text(actionText, actionX + actionW / 2, rBoxTop + 39, { align: "center" });
    }

    // Segmented band strip
    const stripY = y + 60;
    const stripH = 5;
    const stripLeft = M + 10;
    const stripRight = M + cardW - 10;
    const stripW = stripRight - stripLeft;
    const rangeMin = 15, rangeMax = 40;
    let cursorX = stripLeft;
    BMI_BANDS_PDF.forEach((b) => {
      const w = ((Math.min(b.max, rangeMax) - Math.max(b.min, rangeMin)) / (rangeMax - rangeMin)) * stripW;
      doc.setFillColor(...b.hex);
      doc.rect(cursorX, stripY, w, stripH, "F");
      cursorX += w;
    });
    const pt = Math.max(0, Math.min(1, (bmi - rangeMin) / (rangeMax - rangeMin)));
    const markerX = stripLeft + pt * stripW;
    doc.setFillColor(...FOREST);
    doc.rect(markerX - 0.7, stripY - 1.6, 1.4, stripH + 3.2, "F");
    doc.setFontSize(5.8);
    doc.setTextColor(...TEXT_MUTED);
    ["<18.5", "18.5-23", "23-25", "25-30", "≥30"].forEach((t, i) => {
      const cx = stripLeft + (i + 0.5) * (stripW / 5);
      doc.text(t, cx, y + 74, { align: "center" });
    });
  } else {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(...TEXT_MUTED);
    doc.text("BMI not captured", M + 10, y + 40);
  }

  // ---- BMR card (right, dark) ----
  const bx = M + cardW + gap;
  doc.setFillColor(...FOREST);
  doc.roundedRect(bx, y, cardW, cardH, 8, 8, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(...COPPER);
  doc.text("BASAL METABOLIC RATE", bx + 10, y + 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.2);
  doc.setTextColor(200, 200, 200);
  doc.text("Harris-Benedict", bx + cardW - 10, y + 13, { align: "right" });

  let bmr = null;
  if (heightCm && weightKg && age) {
    const male = (13.397 * weightKg) + (4.799 * heightCm) - (5.677 * age) + 88.362;
    const female = (9.247 * weightKg) + (3.098 * heightCm) - (4.330 * age) + 447.593;
    bmr = gender === "female" ? female : gender === "male" ? male : (male + female) / 2;
    bmr = Math.round(bmr);
  }

  if (bmr != null) {
    // Big number
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.text(bmr.toLocaleString(), bx + 10, y + 33);
    // Sub label placed BELOW the number (not beside) to avoid overlap
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(200, 200, 200);
    doc.text("kcal/day at rest", bx + 10, y + 42);
    // Activity multipliers row
    const mults = [
      { l: "Sedentary", m: 1.2 },
      { l: "Moderate",  m: 1.55 },
      { l: "Active",    m: 1.725 },
    ];
    const pillY = y + 52;
    const pillH = 20;
    const totalW = cardW - 20;
    const pw = (totalW - 8) / 3;
    mults.forEach((p, i) => {
      const px = bx + 10 + i * (pw + 4);
      doc.setFillColor(30, 60, 45);
      doc.roundedRect(px, pillY, pw, pillH, 4, 4, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(255, 255, 255);
      doc.text(Math.round(bmr * p.m).toLocaleString(), px + pw / 2, pillY + 9, { align: "center" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.8);
      doc.setTextColor(200, 200, 200);
      doc.text(p.l.toUpperCase(), px + pw / 2, pillY + 16, { align: "center" });
    });
  } else {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(200, 200, 200);
    doc.text("Insufficient data for BMR", bx + 10, y + 40);
  }

  return y + cardH + 8;
}

function drawJourney(doc, y, M, W, result, bandArr) {
  sectionHeader(doc, "YOUR LIVER HEALTH JOURNEY", M, y, W, M);
  y += 16;

  const nodes = [
    { title: "Today", caption: `${result.score}/100 · ${result.label}`, icon: "score" },
    { title: "Next step", caption: NEXT_ACTION_BY_BAND[result.band]?.title || "Book Precision Assessment", icon: "arrow" },
    { title: "Follow-up", caption: result.follow_up?.label || "Recheck in 12 months", icon: "calendar" },
  ];
  const nodeCount = nodes.length;
  const centerYs = y + 20;
  // Inset so the outer captions never clip against page edges.
  const inset = 70;
  const trackLeft = M + inset;
  const trackRight = W - M - inset;
  const step = (trackRight - trackLeft) / (nodeCount - 1);
  const captionW = Math.max(80, step - 12);
  // Base connecting line
  doc.setDrawColor(...BEIGE);
  doc.setLineWidth(1.4);
  doc.line(trackLeft + 16, centerYs, trackRight - 16, centerYs);

  nodes.forEach((n, i) => {
    const cx = trackLeft + i * step;
    // Node bg
    doc.setFillColor(...WHITE);
    doc.setDrawColor(...bandArr);
    doc.setLineWidth(1.8);
    doc.circle(cx, centerYs, 16, "FD");
    if (n.icon === "score") {
      doc.setTextColor(...FOREST);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(String(result.score), cx, centerYs + 3, { align: "center" });
    } else if (n.icon === "arrow") {
      drawArrow(doc, cx, centerYs, 10, FOREST);
    } else {
      drawCalendar(doc, cx, centerYs, 10, FOREST);
    }
    // Title
    doc.setTextColor(...COPPER);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.text(n.title.toUpperCase(), cx, centerYs + 30, { align: "center" });
    // Caption
    doc.setTextColor(...FOREST);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const lines = doc.splitTextToSize(n.caption, captionW);
    doc.text(lines.slice(0, 2), cx, centerYs + 42, { align: "center", lineHeightFactor: 1.3 });
  });

  return centerYs + 54;
}

// ------- PAGE 2 renderers -------

function drawNextAction(doc, y, M, W, result, contact, answers, bandArr) {
  const cfg = NEXT_ACTION_BY_BAND[result.band] || NEXT_ACTION_BY_BAND.green;
  // Squeezed: card is now shorter (48pt vs 64pt) and the copy stacks
  // tighter to reclaim vertical space for the disclaimer/footer.
  const cardH = 48;
  const useDark = !cfg.hideCta;

  if (useDark) {
    doc.setFillColor(...FOREST);
    doc.roundedRect(M, y, W - M * 2, cardH, 10, 10, "F");
  } else {
    doc.setFillColor(...BEIGE);
    doc.roundedRect(M, y, W - M * 2, cardH, 10, 10, "F");
  }

  // Icon disc on the left — a touch smaller
  const discX = M + 26;
  const discY = y + cardH / 2;
  doc.setFillColor(...bandArr);
  doc.circle(discX, discY, 14, "F");
  if (cfg.icon === "alert") {
    doc.setFillColor(...WHITE);
    doc.triangle(discX - 6, discY + 5, discX + 6, discY + 5, discX, discY - 6, "F");
    doc.setTextColor(...bandArr);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("!", discX, discY + 3, { align: "center" });
  } else if (cfg.icon === "scan") {
    doc.setDrawColor(...WHITE);
    doc.setLineWidth(1.4);
    doc.circle(discX, discY, 5.5, "S");
    doc.circle(discX, discY, 2.8, "S");
  } else if (cfg.icon === "stethoscope") {
    drawCheck(doc, discX, discY, 8, WHITE);
  } else {
    doc.setDrawColor(...WHITE);
    doc.setLineWidth(1.4);
    doc.roundedRect(discX - 5, discY - 6, 10, 12, 3, 3, "S");
    drawCheck(doc, discX, discY + 1, 5, WHITE);
  }

  // Title (single line, slightly smaller)
  doc.setTextColor(...(useDark ? WHITE : FOREST));
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(cfg.title, M + 50, y + 20);

  // Bottom row: price + WhatsApp CTA (or follow-up note for green)
  if (!cfg.hideCta) {
    doc.setTextColor(...COPPER);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(PRICE_LABEL, M + 50, y + cardH - 12);
    // Right side WhatsApp link
    doc.setTextColor(180, 200, 195);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.text("CHAT ON WHATSAPP", W - M - 12, y + 20, { align: "right" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...COPPER);
    const url = buildWhatsAppUrl(result, contact, answers) || `https://wa.me/${WHATSAPP_NUMBER}`;
    doc.textWithLink(CONTACT_PHONE, W - M - 12, y + cardH - 12, { url, align: "right" });
  } else {
    doc.setTextColor(...FOREST);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7.5);
    doc.text(`Follow-up: ${result.follow_up?.label || "Recheck in 12 months"}.`, M + 50, y + cardH - 12);
  }

  return y + cardH + 8;
}

function drawRecommendations(doc, y, M, W, result, bandArr) {
  sectionHeader(doc, "YOUR PERSONALIZED RECOMMENDATIONS", M, y, W, M);
  y += 12;
  // Two-column bullet layout to save vertical space.
  const items = (result.recommendations || []).slice(0, 4);
  const colGap = 16;
  const colW = (W - M * 2 - colGap) / 2;
  const rowH = 30;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const rows = Math.ceil(items.length / 2);
  items.forEach((r, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const cx = M + col * (colW + colGap);
    const cy = y + row * rowH;
    doc.setFillColor(...bandArr);
    doc.circle(cx + 3, cy + 5, 2, "F");
    doc.setTextColor(...TEXT_DARK);
    const lines = doc.splitTextToSize(r, colW - 12).slice(0, 3);
    doc.text(lines, cx + 12, cy + 7, { lineHeightFactor: 1.35 });
  });
  return y + rows * rowH + 6;
}

function drawRedFlagsBox(doc, y, M, W, labels) {
  const boxTop = y;
  const bodyH = 18 + labels.length * 11 + 6;
  doc.setFillColor(254, 226, 226);
  doc.setDrawColor(239, 68, 68);
  doc.setLineWidth(1);
  doc.roundedRect(M, boxTop, W - M * 2, bodyH, 6, 6, "FD");
  // Warning triangle
  doc.setFillColor(239, 68, 68);
  doc.triangle(M + 12, boxTop + 15, M + 22, boxTop + 15, M + 17, boxTop + 6, "F");
  doc.setTextColor(185, 28, 28);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.text("MEDICAL RED FLAGS REPORTED", M + 28, boxTop + 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  let ry = boxTop + 26;
  labels.forEach((f) => {
    doc.setFillColor(239, 68, 68);
    doc.circle(M + 16, ry - 3, 1.6, "F");
    doc.text(f, M + 22, ry);
    ry += 11;
  });
  return boxTop + bodyH + 10;
}

function drawPhilosophy(doc, y, M, W, H, reserveOverride) {
  // Bottom-anchor the philosophy strip so it never collides with the
  // disclaimer/footer. `reserveOverride` (in points) is computed from the
  // real disclaimer text height by measureDisclaimerReserve() so the pathway
  // card always sits above the disclaimer regardless of how long the text is.
  const cardH = 40;
  const reserve = reserveOverride || 62;
  const cardY = Math.max(y, H - reserve - cardH);
  doc.setFillColor(...BEIGE);
  doc.roundedRect(M, cardY, W - M * 2, cardH, 10, 10, "F");
  const n = PHILOSOPHY.length;
  const step = (W - M * 2) / n;
  PHILOSOPHY.forEach((p, i) => {
    const cx = M + step * (i + 0.5);
    // Numbered dot
    doc.setFillColor(...COPPER);
    doc.circle(cx, cardY + 11, 5.5, "F");
    doc.setTextColor(...WHITE);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.text(String(i + 1), cx, cardY + 13.2, { align: "center" });
    // Label
    doc.setTextColor(...FOREST);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(p.label, cx, cardY + 26, { align: "center" });
    // Body
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.2);
    doc.setTextColor(...TEXT_MUTED);
    doc.text(p.body, cx, cardY + 35, { align: "center" });
    // Divider between items
    if (i < n - 1) {
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(1);
      doc.line(M + step * (i + 1), cardY + 6, M + step * (i + 1), cardY + 34);
    }
  });
  return cardY + cardH + 4;
}

// Full disclaimer text — kept as a module-level constant so drawPhilosophy
// can measure its rendered height and reserve exactly the right amount of
// bottom-of-page space (no more, no less). Prevents the pathway card from
// overlapping the disclaimer when its text grows.
const DISCLAIMER_TEXT =
  "This is an educational report generated from a self-reported screening tool — it is not a medical diagnosis. "
  + "A lower screening score does not exclude fatty liver or fibrosis. Please consult a qualified physician for clinical decisions. "
  + `*${LIVER_AGE_DISCLAIMER}`;
const DISCLAIMER_FONT_SIZE = 6.5;
const DISCLAIMER_LINE_H = 8;

function measureDisclaimerReserve(doc, M, W) {
  // Reserved bottom-of-page space =
  //   footer separator/text zone (30pt from H)
  //   + disclaimer text height
  //   + divider gap (6pt)
  //   + safety (8pt)
  doc.setFont("helvetica", "italic");
  doc.setFontSize(DISCLAIMER_FONT_SIZE);
  const lines = doc.splitTextToSize(DISCLAIMER_TEXT, W - M * 2);
  return 30 + lines.length * DISCLAIMER_LINE_H + 6 + 8;
}

function drawDisclaimer(doc, y, M, W, H) {
  // Anchor the disclaimer just above the footer separator (H - 24) so it
  // never collides with the brand footer line no matter how tall the plan
  // section grows above.
  doc.setFont("helvetica", "italic");
  doc.setFontSize(DISCLAIMER_FONT_SIZE);
  const dl = doc.splitTextToSize(DISCLAIMER_TEXT, W - M * 2);
  const totalH = dl.length * DISCLAIMER_LINE_H;
  const anchorY = H - 30 - totalH; // 30pt above bottom, above footer at H-24
  // Divider line just above disclaimer
  doc.setDrawColor(...BEIGE);
  doc.setLineWidth(0.5);
  doc.line(M, anchorY - 6, W - M, anchorY - 6);
  doc.setTextColor(...TEXT_MUTED);
  doc.text(dl, M, anchorY, { lineHeightFactor: 1.35 });
}

function drawFooter(doc, M, W, H, page = 1, totalPages = 1) {
  doc.setDrawColor(...BEIGE);
  doc.setLineWidth(0.6);
  doc.line(M, H - 24, W - M, H - 24);
  doc.setTextColor(...TEXT_MUTED);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7.5);
  doc.text("2 minutes. One score. Know your liver risk.", M, H - 12);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(...FOREST);
  doc.text("SuperAge Health™  ·  Precision Organ Medicine", W - M, H - 12, { align: "right" });
  if (totalPages > 1) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...TEXT_MUTED);
    doc.text(`Page ${page} of ${totalPages}`, W / 2, H - 12, { align: "center" });
  }
}
